import {
  InputStyle,
  InputStyleWrapper,
  InputStyleWrapperPropsType,
  InputValidationMessage,
  Select,
  SelectInputProps,
  Tooltip,
} from '@getvim/atomic-ui';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { SelectOption } from '../../../types';

import './SelectLabelValueOption.less';

type SelectLabelValueOptionProps = {
  disabled?: boolean;
  items: SelectOption[];
  loading?: boolean;
  selected: SelectOption | undefined;
  onChange: (newValue: SelectOption) => void;
  wrapperProps?: Partial<InputStyleWrapperPropsType>;
  selectProps: Partial<SelectInputProps> & Pick<SelectInputProps, 'id' | 'name' | 'placeholder'>;
  isDirty?: boolean;
  validationError?: string;
  className?: string;
  valueField?: string;
};

const SelectLabelValueOption: React.FC<SelectLabelValueOptionProps> = ({
  disabled = false,
  items = [],
  onChange,
  loading = false,
  selected,
  wrapperProps = {},
  selectProps,
  isDirty,
  validationError,
  className,
  valueField = 'value',
}) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SelectOption | undefined>();
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>([]);
  const [loadingOnFilter, setLoadingOnFilter] = useState<boolean>(false);

  useEffect(() => {
    const [firstOption, ...restOptions] = options;
    if (!disabled && firstOption && !restOptions.length && firstOption.value !== selected?.value) {
      onChange(firstOption);
    }
  }, [options, onChange, selected, disabled]);

  const filterOptionsByString = useCallback(
    (searchString: string) => {
      setLoadingOnFilter(true);
      if (!searchString) {
        setFilteredOptions(options);
      } else {
        const filtered = options.filter((option) =>
          option.label.toLowerCase().includes(searchString.toLowerCase()),
        );
        setFilteredOptions(filtered);
      }
      setLoadingOnFilter(false);
    },
    [options],
  );

  const onSearch = ({ state, methods }) => {
    const searchString = methods.safeString(state.search);
    filterOptionsByString(searchString);
  };

  useEffect(() => {
    if (selected) {
      setSelectedOption(selected);
    } else {
      setFilteredOptions(options);
      setSelectedOption(undefined);
    }
  }, [selected, options]);

  useEffect(() => {
    setOptions(items);
    setFilteredOptions(items);
  }, [items, onChange]);

  return (
    <InputStyleWrapper
      className={classNames(`input-wrapper ${wrapperProps.className}`)}
      leftIcon={wrapperProps.leftIcon}
      rightIcon={<></>}
      inputStyle={InputStyle.pillMedium}
      hasError={isDirty && !!validationError}
    >
      <div
        className={classNames('input select-label-value-option', {
          'no-icon-input': !wrapperProps.leftIcon,
        })}
      >
        <Tooltip
          tooltipContent={selected?.label}
          hideTooltip={!selected?.label}
          key={selected?.value}
          conditionalTooltip
          contentMaxLength={5}
          className={className}
        >
          <Select
            placeholder={selectProps.placeholder}
            valueField={valueField}
            id={selectProps.id}
            name={selectProps.name}
            options={filteredOptions}
            onChange={(newOptions) => {
              filterOptionsByString(newOptions?.[0]?.label);
              onChange(newOptions?.[0]);
            }}
            noDataLabel={selectProps.noDataLabel}
            error={selectProps.error}
            noInputLabel={selectProps.noInputLabel}
            noResultsLabel={selectProps.noResultsLabel}
            isDisabled={disabled}
            loading={loading || loadingOnFilter}
            searchFn={onSearch}
            values={selectedOption ? [selectedOption] : []}
            hideValueOnInput
            sortOptions
            clearable
            closeOnSelect
            keepSelectedInList
            showChevronIcon
          />
        </Tooltip>
      </div>
      {isDirty && !!validationError ? (
        <InputValidationMessage>{validationError}</InputValidationMessage>
      ) : null}
    </InputStyleWrapper>
  );
};

export default SelectLabelValueOption;
